import { useCallback, useEffect, useRef, useState } from 'react';
import { joinQueue, listenToQueue } from '../firebase/queue';
import { listenToSession, type LiveSession } from '../firebase/sessions';
import { positionOf, queuedStudents, type QueuedStudent } from './queue';

export type WaitingRoom = {
  /** 1-based place in line, or null while unknown or out of the queue. */
  position: number | null;
  /** True once this student was in the queue and then vanished from it. */
  dropped: boolean;
  /** Also the retry after a failed join. */
  rejoin: () => void;
  /**
   * The queue write itself failed — offline, or refused by the rules. It used
   * to be swallowed: the screen said "joining…" forever while the student was
   * in no queue at all.
   */
  joinFailed: boolean;
  /** The session as the server last described it: who teaches, which mode. */
  session: LiveSession | null;
  /** Stops beating and deletes the queue entry. Navigation is the caller's. */
  leave: () => void;
};

/**
 * Everything a waiting room does, minus how it draws.
 *
 * Shared by both clients because every rule in here was learned from a real
 * failure, and a second implementation would relearn them one at a time:
 *
 * - Admission is what the SERVER writes onto the session document; the queue
 *   subcollection says nothing about it. Watching only the queue meant the
 *   معلم admitted a student, the server opened the room, and the student went
 *   on reading their queue position while the معلم sat alone.
 * - The admission latch: the session document is rewritten on every turn
 *   change and every recount, and acting on each would open a second call on
 *   top of the first.
 * - `dropped` uses the REAP threshold, not the stale one. Being stale (15s)
 *   only stops you counting toward the discovery badge; being reaped (120s)
 *   means your document is gone. Judging it by staleness told a phone that had
 *   been backgrounded twenty seconds that its connection had dropped — and the
 *   rejoin it offered writes a fresh joinTime, sending them to the back of a
 *   queue they were near the front of.
 * - It is latched on having BEEN in the queue: before the first join lands,
 *   a null position means "not yet", not "removed". And the server deletes the
 *   queue entry as part of admitting someone, so vanishing is also what
 *   success looks like — hence the enteredCall guard.
 */
export function useWaitingRoom(params: {
  sessionId: string | undefined;
  studentId: string;
  studentName: string;
  onAdmitted: () => void;
}): WaitingRoom {
  const { sessionId, studentId, studentName, onAdmitted } = params;
  const [queue, setQueue] = useState<QueuedStudent[]>([]);
  const [dropped, setDropped] = useState(false);
  const [joinFailed, setJoinFailed] = useState(false);
  const [session, setSession] = useState<LiveSession | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const wasInQueue = useRef(false);
  const enteredCall = useRef(false);

  // So the session listener never calls a stale copy of the caller's handler.
  const admittedRef = useRef(onAdmitted);
  admittedRef.current = onAdmitted;

  const join = useCallback(() => {
    if (!sessionId) return () => undefined;
    let cancelled = false;
    void joinQueue(sessionId, studentId, studentName).then((stop) => {
      // Unmounting before the join resolves must still leave the queue.
      if (cancelled) { stop(); return; }
      stopRef.current = stop;
    }, () => {
      // Said out loud, with a retry: silence here left a student watching
      // "joining…" while they were in no queue at all.
      if (!cancelled) setJoinFailed(true);
    });
    return () => { cancelled = true; };
  }, [sessionId, studentId, studentName]);

  useEffect(() => {
    if (!sessionId) return undefined;
    const cancelJoin = join();
    const unsub = listenToQueue(sessionId, setQueue);
    return () => {
      cancelJoin();
      stopRef.current?.();
      stopRef.current = null;
      unsub();
    };
  }, [join, sessionId]);

  useEffect(() => {
    if (!sessionId) return undefined;
    return listenToSession(sessionId, (live) => {
      setSession(live);
      if (enteredCall.current) return;
      if (!live || !live.activeCall) return;
      if (!live.activeCall.participants.includes(studentId)) return;
      enteredCall.current = true;
      stopRef.current?.();
      stopRef.current = null;
      admittedRef.current();
    });
  }, [sessionId, studentId]);

  const position = positionOf(queuedStudents(queue, Date.now()), studentId);

  useEffect(() => {
    if (enteredCall.current) return;
    if (position !== null) {
      wasInQueue.current = true;
      setDropped(false);
    } else if (wasInQueue.current) {
      setDropped(true);
    }
  }, [position]);

  const rejoin = () => {
    stopRef.current?.();
    stopRef.current = null;
    wasInQueue.current = false;
    setDropped(false);
    setJoinFailed(false);
    join();
  };

  const leave = () => {
    stopRef.current?.();
    stopRef.current = null;
  };

  return { position, dropped, rejoin, leave, joinFailed, session };
}
